import { useMemo } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import SyncIcon from '@mui/icons-material/Sync';
import {
  SDataTable,
  type SDataTableColumnDef,
} from '@modules/ui-components/dialogs/SDataTable';
import { useTikTokAdsConnect } from '../hooks/useTikTokAdsConnect';
import { TIKTOK_ADS_TEST_IDS } from '../testIds';
import type { TikTokAdsAdvertiser, TikTokAdsConnectApi } from '../types';

export interface TikTokAdsIntegrationContentProps {
  projectId: string;
  showHeader?: boolean;
  service: TikTokAdsConnectApi;
}

function accessPath(advertiser: TikTokAdsAdvertiser): string {
  if (advertiser.accessPath === 'sandbox_token') return 'Sandbox token';
  if (advertiser.accessPath === 'business_center') {
    return advertiser.businessCenterId ? `Business Center ${advertiser.businessCenterId}` : 'Business Center';
  }
  return 'Direct';
}

function isStale(value?: string): boolean {
  if (!value) return false;
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) && Date.now() - timestamp > 24 * 60 * 60 * 1_000;
}

function formatTimestamp(value?: string): string {
  if (!value) return 'Never';
  const timestamp = Date.parse(value);
  return Number.isFinite(timestamp) ? new Date(timestamp).toLocaleString() : value;
}

function loadingIcon(active: boolean, fallback: React.ReactNode): React.ReactNode {
  return active ? <CircularProgress size={16} color="inherit" /> : fallback;
}

function evidenceString(row: Record<string, unknown>, keys: string[]): string | undefined {
  for (const key of keys) {
    const value = row[key];
    if (typeof value === 'string' && value.trim()) return value;
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return undefined;
}

function entityEvidence(row: Record<string, unknown>): { label: string; id: string; grain: string } {
  const kind = evidenceString(row, ['entityKind']) ?? 'unknown';
  const labels: Record<string, { label: string; grain: string; ids: string[] }> = {
    campaign: { label: 'Campaign', grain: 'campaign', ids: ['campaignId', 'campaign_id'] },
    ad_group: { label: 'Ad group', grain: 'ad_group', ids: ['adGroupId', 'adgroup_id', 'ad_group_id'] },
    ad: { label: 'Manual ad', grain: 'ad', ids: ['adId', 'ad_id'] },
    ad_container: { label: 'Smart+ container', grain: 'ad_container', ids: ['adIdV2', 'ad_id_v2', 'adId', 'ad_id'] },
    creative_artifact: { label: 'Creative material', grain: 'creative_artifact', ids: ['materialId', 'material_id'] },
  };
  const metadata = labels[kind] ?? { label: 'Provider entity', grain: kind, ids: [] };
  return {
    label: metadata.label,
    grain: metadata.grain,
    id: evidenceString(row, ['providerEntityId', ...metadata.ids, 'id']) ?? 'ID unavailable',
  };
}

function deliveryEvidence(row: Record<string, unknown>): {
  label: string;
  id: string;
  facts: string[];
} {
  const family = evidenceString(row, ['campaignFamily', 'campaign_family']);
  const label = family === 'smart_plus' ? 'Smart+ container' : family === 'manual' ? 'Manual ad' : 'Delivery row';
  const id = evidenceString(row, ['decisionUnitId', 'decision_unit_id', 'adIdV2', 'ad_id_v2', 'adId', 'ad_id'])
    ?? 'ID unavailable';
  const facts: string[] = [];
  const spend = evidenceString(row, ['spendDecimal', 'spend_decimal', 'spend']);
  const currency = evidenceString(row, ['currency']);
  const currencyExponent = evidenceString(row, ['currencyExponent', 'currency_exponent']);
  const impressions = evidenceString(row, ['impressions']);
  const clicks = evidenceString(row, ['clicks']);
  const conversions = evidenceString(row, ['conversions', 'conversion']);
  const finality = evidenceString(row, ['providerFinality', 'provider_finality', 'finality']);
  const reportDate = evidenceString(row, ['reportDate', 'report_date']);
  if (spend) facts.push(`Spend ${spend}${currency ? ` ${currency}` : ''}`);
  if (currencyExponent) facts.push(`currency exponent ${currencyExponent}`);
  if (impressions) facts.push(`${impressions} impressions`);
  if (clicks) facts.push(`${clicks} clicks`);
  if (conversions) facts.push(`${conversions} conversions`);
  if (finality) facts.push(`finality ${finality}`);
  if (reportDate) facts.push(`report date ${reportDate}`);
  return { label, id, facts };
}

export function TikTokAdsIntegrationContent({
  projectId,
  showHeader = false,
  service,
}: TikTokAdsIntegrationContentProps) {
  const tikTok = useTikTokAdsConnect(projectId, service);
  const columns = useMemo<SDataTableColumnDef<TikTokAdsAdvertiser>[]>(() => [
    {
      accessorKey: 'displayName',
      header: 'Advertiser',
      size: 180,
      minSize: 160,
      grow: false,
      Cell: ({ row }) => (
        <Stack spacing={0.25}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {row.original.displayName || 'Unnamed advertiser'}
          </Typography>
          {row.original.status !== 'active' && (
            <Typography variant="caption" color="text.secondary">
              {row.original.status === 'disabled' ? 'Disabled' : 'Status unknown'}
            </Typography>
          )}
        </Stack>
      ),
    },
    {
      accessorKey: 'advertiserId',
      header: 'Advertiser ID',
      size: 175,
      minSize: 155,
      grow: false,
    },
    {
      id: 'accessPath',
      header: 'Access path',
      size: 200,
      minSize: 180,
      grow: false,
      accessorFn: accessPath,
      Cell: ({ row }) => <Chip size="small" variant="outlined" label={accessPath(row.original)} />,
    },
    {
      accessorKey: 'currency',
      header: 'Currency',
      size: 100,
      minSize: 90,
      grow: false,
      Cell: ({ cell }) => cell.getValue<string>() || '—',
    },
    {
      accessorKey: 'timeZone',
      header: 'Time zone',
      size: 155,
      minSize: 140,
      grow: false,
      Cell: ({ cell }) => cell.getValue<string>() || '—',
    },
  ], []);

  if (tikTok.loading) {
    return (
      <Box
        data-testid={TIKTOK_ADS_TEST_IDS.loading}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 5 }}
      >
        <CircularProgress size={32} aria-label="Loading TikTok Ads connection" />
      </Box>
    );
  }

  const runtime = tikTok.runtime;
  const activeConnection = tikTok.activeConnection;
  const isConnected = activeConnection?.state === 'connected';
  const selectedCount = tikTok.selectedAdvertiserIds.length;
  const selectionHelp = selectedCount === 0
    ? 'Select at least one active advertiser to continue.'
    : selectedCount > 100
      ? 'Select no more than 100 advertisers.'
      : tikTok.isDirty
        ? `${selectedCount} advertiser${selectedCount === 1 ? '' : 's'} ready to save.`
        : 'Saved advertiser selection is current.';
  const saveDisabled = !tikTok.isDirty || selectedCount < 1 || selectedCount > 100 || tikTok.action !== null;
  const hasSavedSelection = tikTok.persistedAdvertiserIds.length > 0 && !tikTok.isDirty;
  const previewPartial = tikTok.entityPreview?.diagnostics.hasMore === true
    || tikTok.deliveryPreview?.providerDiagnostics.hasMore === true
    || Boolean(tikTok.entityPreview?.diagnostics.warnings?.length)
    || Boolean(tikTok.deliveryPreview?.providerDiagnostics.warnings?.length);
  const lastSyncStale = isStale(activeConnection?.lastSuccessfulManualSyncAt);

  return (
    <Stack spacing={2.5} data-testid={TIKTOK_ADS_TEST_IDS.root}>
      {showHeader && (
        <Box>
          <Typography variant="h6">TikTok Ads</Typography>
          <Typography variant="body2" color="text.secondary">
            Select advertisers and synchronize read-only campaign and delivery evidence.
          </Typography>
        </Box>
      )}

      {tikTok.notice && (
        <Alert severity="success" onClose={tikTok.clearNotice}>
          {tikTok.notice}
        </Alert>
      )}
      {tikTok.error && (
        <Alert
          severity="error"
          data-testid={TIKTOK_ADS_TEST_IDS.error}
          onClose={tikTok.clearError}
          action={!tikTok.isDirty ? (
            <Button color="inherit" size="small" onClick={() => void tikTok.refreshConnections()}>
              Retry
            </Button>
          ) : undefined}
        >
          {tikTok.error}
        </Alert>
      )}

      {!runtime?.connectorEnabled && (
        <Alert
          severity={runtime?.rollbackActive ? 'warning' : 'info'}
          data-testid={TIKTOK_ADS_TEST_IDS.statusAlert}
        >
          <AlertTitle>TikTok Ads connector is unavailable</AlertTitle>
          {runtime?.rollbackActive
            ? 'The connector is temporarily paused by an operator safety control.'
            : runtime?.unavailableReason || 'An administrator must enable the read-only connector before this project can connect.'}
        </Alert>
      )}

      {runtime?.connectorEnabled && (!runtime.projectEligible || !runtime.credentialReady) && !activeConnection && (
        <Alert
          severity={runtime.projectEligible ? 'info' : 'warning'}
          data-testid={TIKTOK_ADS_TEST_IDS.statusAlert}
        >
          <AlertTitle>
            {runtime.projectEligible ? 'TikTok Ads configuration is incomplete' : 'This project is not eligible'}
          </AlertTitle>
          {runtime.unavailableReason || (runtime.projectEligible
            ? 'An administrator must finish the provider credential configuration before this project can connect.'
            : 'TikTok Ads sandbox access is limited to approved DEV and STAGE projects.')}
        </Alert>
      )}

      {runtime?.connectorEnabled && runtime.projectEligible && runtime.credentialReady && !activeConnection && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 } }}>
          <Stack spacing={2} alignItems="flex-start">
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Chip
                size="small"
                color="primary"
                variant="outlined"
                label={runtime.apiEnvironment === 'sandbox' ? 'Sandbox' : 'Production'}
                data-testid={TIKTOK_ADS_TEST_IDS.environmentChip}
              />
              <Chip size="small" variant="outlined" label="Read-only" />
            </Stack>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Connect TikTok Ads
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {runtime.apiEnvironment === 'sandbox'
                  ? 'Bind this eligible project to the administrator-configured TikTok sandbox advertiser.'
                  : 'Authorize TikTok Ads reporting access. Segmently will not modify campaigns, budgets, bids, targeting, creatives, or account settings.'}
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={loadingIcon(tikTok.action === 'connect', <AddLinkIcon />)}
              disabled={tikTok.action !== null}
              onClick={() => void tikTok.connect()}
              data-testid={TIKTOK_ADS_TEST_IDS.connectButton}
            >
              {runtime.apiEnvironment === 'sandbox' ? 'Connect sandbox' : 'Connect TikTok Ads'}
            </Button>
          </Stack>
        </Paper>
      )}

      {runtime?.connectorEnabled && activeConnection?.state === 'disconnected' && (
        <Alert
          severity="info"
          data-testid={TIKTOK_ADS_TEST_IDS.statusAlert}
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={tikTok.action !== null || !runtime.projectEligible || !runtime.credentialReady}
              onClick={() => void tikTok.connect()}
              data-testid={TIKTOK_ADS_TEST_IDS.connectButton}
            >
              Connect
            </Button>
          )}
        >
          <AlertTitle>{activeConnection.displayName || 'TikTok Ads connection'}</AlertTitle>
          This connection is disconnected. Reconnect it to discover advertisers.
        </Alert>
      )}

      {runtime?.connectorEnabled && activeConnection?.state === 'needs_reconnect' && (
        <Alert
          severity="warning"
          data-testid={TIKTOK_ADS_TEST_IDS.statusAlert}
          action={(
            <Button
              color="inherit"
              size="small"
              disabled={tikTok.action !== null}
              onClick={() => void tikTok.connect()}
              data-testid={TIKTOK_ADS_TEST_IDS.connectButton}
            >
              Reconnect
            </Button>
          )}
        >
          <AlertTitle>TikTok authorization needs attention</AlertTitle>
          Reconnect before reading provider data. Existing advertiser selections remain recorded.
        </Alert>
      )}

      {runtime?.connectorEnabled && isConnected && activeConnection && (
        <Paper variant="outlined" sx={{ p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2.5}>
            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', md: 'center' }}
              justifyContent="space-between"
            >
              <Box>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center">
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {activeConnection.displayName || activeConnection.connectionId}
                  </Typography>
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={activeConnection.apiEnvironment === 'sandbox' ? 'Sandbox' : 'Production'}
                    data-testid={TIKTOK_ADS_TEST_IDS.environmentChip}
                  />
                  <Chip size="small" variant="outlined" label="Read-only" />
                  <Chip size="small" variant="outlined" label={`${activeConnection.selectedAdvertiserIds.length} selected`} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  {activeConnection.credentialSource === 'provider_sandbox'
                    ? 'Internal sandbox binding. This proves bounded sandbox reads, not production OAuth.'
                    : `OAuth connection · ${activeConnection.sourceApiVersion}`}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={<RefreshIcon />}
                  disabled={tikTok.isDirty || tikTok.advertisersLoading || tikTok.action !== null}
                  onClick={() => void tikTok.refreshAdvertisers()}
                  data-testid={TIKTOK_ADS_TEST_IDS.refreshButton}
                >
                  Refresh
                </Button>
                <Button
                  size="small"
                  color="inherit"
                  startIcon={loadingIcon(tikTok.action === 'disconnect', <LinkOffIcon />)}
                  disabled={tikTok.isDirty || tikTok.action !== null}
                  onClick={() => void tikTok.disconnect()}
                  data-testid={TIKTOK_ADS_TEST_IDS.disconnectButton}
                >
                  Disconnect
                </Button>
              </Stack>
            </Stack>

            {tikTok.connections.length > 1 && (
              <FormControl size="small" sx={{ maxWidth: 440 }} disabled={tikTok.isDirty || tikTok.action !== null}>
                <InputLabel id="tiktok-ads-connection-label">Authorized connection</InputLabel>
                <Select
                  labelId="tiktok-ads-connection-label"
                  label="Authorized connection"
                  value={tikTok.activeConnectionId}
                  onChange={(event) => tikTok.chooseConnection(event.target.value)}
                  data-testid={TIKTOK_ADS_TEST_IDS.connectionSelect}
                >
                  {tikTok.connections.map((connection) => (
                    <MenuItem key={connection.connectionId} value={connection.connectionId}>
                      {connection.displayName || connection.connectionId}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {activeConnection.credentialSource === 'provider_sandbox' && (
              <Alert severity="info">
                <AlertTitle>Internal sandbox connection</AlertTitle>
                The token remains in the administrator credential vault and is never returned to this browser.
              </Alert>
            )}

            {tikTok.isDirty && (
              <Alert severity="info">
                Save or discard this advertiser selection before refreshing, previewing, synchronizing, or disconnecting.
              </Alert>
            )}

            {tikTok.advertisersLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 5 }}>
                <CircularProgress size={32} aria-label="Discovering TikTok Ads advertisers" />
              </Box>
            ) : tikTok.advertisers.length === 0 ? (
              <Alert severity="info">
                <AlertTitle>No advertisers found</AlertTitle>
                This connection currently exposes no eligible TikTok advertiser. The connection remains active.
              </Alert>
            ) : (
              <Box data-testid={TIKTOK_ADS_TEST_IDS.advertiserTable} sx={{ overflowX: 'auto' }}>
                <SDataTable<TikTokAdsAdvertiser>
                  columns={columns}
                  data={tikTok.advertisers}
                  layoutMode="grid-no-grow"
                  enableTopToolbar={false}
                  enableSelectAll={tikTok.advertisers.length <= 100}
                  getRowId={(row) => row.advertiserId}
                  enableRowSelection={(row) => row.original.status !== 'disabled' && (
                    tikTok.selectedAdvertiserIds.length < 100
                    || tikTok.rowSelection[row.original.advertiserId] === true
                  )}
                  muiSelectCheckboxProps={({ row }) => ({
                    inputProps: {
                      'aria-label': `Select ${row.original.displayName || row.original.advertiserId}`,
                      'data-testid': TIKTOK_ADS_TEST_IDS.advertiserCheckbox(row.original.advertiserId),
                    },
                  })}
                  enablePagination={tikTok.advertisers.length > 25}
                  initialState={{ pagination: { pageIndex: 0, pageSize: 25 } }}
                  state={{ rowSelection: tikTok.rowSelection }}
                  onRowSelectionChange={tikTok.setRowSelection}
                  muiTableBodyRowProps={({ row }) => ({
                    'data-testid': TIKTOK_ADS_TEST_IDS.advertiserRow(row.original.advertiserId),
                  } as any)}
                  muiTableProps={{ size: 'small', sx: { minWidth: 850 } }}
                  muiTableContainerProps={{ sx: { maxHeight: 440 } }}
                />
              </Box>
            )}

            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography
                  variant="body2"
                  color={selectedCount < 1 || selectedCount > 100 ? 'error' : 'text.secondary'}
                  data-testid={TIKTOK_ADS_TEST_IDS.selectedCount}
                >
                  {selectionHelp}
                </Typography>
                {tikTok.isDirty && (
                  <Button size="small" color="inherit" onClick={tikTok.discardSelection} disabled={tikTok.action !== null}>
                    Discard changes
                  </Button>
                )}
              </Stack>
              <Button
                variant="contained"
                startIcon={loadingIcon(tikTok.action === 'save', <SaveIcon />)}
                disabled={saveDisabled}
                onClick={() => void tikTok.saveSelection()}
                data-testid={TIKTOK_ADS_TEST_IDS.saveButton}
              >
                Save advertiser selection
              </Button>
            </Stack>

            {hasSavedSelection && (
              <Stack spacing={2} sx={{ pt: 1 }}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    Bounded reporting preview
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Validate the selected advertiser before synchronization. These actions do not change TikTok Ads.
                  </Typography>
                </Box>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'flex-start' }}>
                  <FormControl size="small" sx={{ minWidth: 260 }}>
                    <InputLabel id="tiktok-preview-advertiser-label">Preview advertiser</InputLabel>
                    <Select
                      labelId="tiktok-preview-advertiser-label"
                      label="Preview advertiser"
                      value={tikTok.previewAdvertiserId}
                      onChange={(event) => tikTok.setPreviewAdvertiserId(event.target.value)}
                      disabled={tikTok.action !== null}
                    >
                      {tikTok.advertisers
                        .filter((advertiser) => tikTok.persistedAdvertiserIds.includes(advertiser.advertiserId))
                        .map((advertiser) => (
                          <MenuItem key={advertiser.advertiserId} value={advertiser.advertiserId}>
                            {advertiser.displayName || advertiser.advertiserId}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                  <TextField
                    size="small"
                    type="date"
                    label="From"
                    value={tikTok.since}
                    onChange={(event) => tikTok.setSince(event.target.value)}
                    disabled={tikTok.action !== null}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    value={tikTok.until}
                    onChange={(event) => tikTok.setUntil(event.target.value)}
                    disabled={tikTok.action !== null}
                    InputLabelProps={{ shrink: true }}
                  />
                </Stack>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  <Button
                    variant="outlined"
                    startIcon={loadingIcon(tikTok.action === 'entity-preview', <SearchIcon />)}
                    disabled={tikTok.action !== null || !tikTok.previewAdvertiserId}
                    onClick={() => void tikTok.previewEntities()}
                    data-testid={TIKTOK_ADS_TEST_IDS.entityPreviewButton}
                  >
                    Preview entities
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={loadingIcon(tikTok.action === 'delivery-preview', <SearchIcon />)}
                    disabled={tikTok.action !== null || !tikTok.previewAdvertiserId || !tikTok.since || !tikTok.until || tikTok.since > tikTok.until}
                    onClick={() => void tikTok.previewDelivery()}
                    data-testid={TIKTOK_ADS_TEST_IDS.deliveryPreviewButton}
                  >
                    Preview delivery
                  </Button>
                </Stack>

                {(tikTok.entityPreview || tikTok.deliveryPreview) && (
                  <Paper
                    variant="outlined"
                    sx={{ p: 2 }}
                    data-testid={TIKTOK_ADS_TEST_IDS.previewSummary}
                  >
                    <Stack spacing={1.5}>
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        {tikTok.entityPreview && (
                          <>
                            <Chip size="small" label={`${tikTok.entityPreview.counts.campaigns} campaigns`} />
                            <Chip size="small" label={`${tikTok.entityPreview.counts.adGroups} ad groups`} />
                            <Chip size="small" label={`${tikTok.entityPreview.counts.manualAds} manual ads`} />
                            <Chip size="small" label={`${tikTok.entityPreview.counts.smartPlusContainers} Smart+ containers`} />
                          </>
                        )}
                        {tikTok.deliveryPreview && (
                          <Chip size="small" label={`${tikTok.deliveryPreview.rows.length} delivery rows`} />
                        )}
                      </Stack>
                      {tikTok.entityPreview && tikTok.entityPreview.entities.length > 0 && (
                        <Box data-testid={TIKTOK_ADS_TEST_IDS.entityEvidence}>
                          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Provider entities</Typography>
                          <Stack spacing={0.75}>
                            {tikTok.entityPreview.entities.slice(0, 12).map((row, index) => {
                              const evidence = entityEvidence(row);
                              return (
                                <Box key={`${evidence.grain}-${evidence.id}-${index}`}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {evidence.label} · {evidence.id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Native grain: {evidence.grain}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                          {tikTok.entityPreview.entities.length > 12 && (
                            <Typography variant="caption" color="text.secondary">
                              Showing 12 of {tikTok.entityPreview.entities.length} bounded preview entities.
                            </Typography>
                          )}
                        </Box>
                      )}
                      {tikTok.deliveryPreview && tikTok.deliveryPreview.rows.length > 0 && (
                        <Box data-testid={TIKTOK_ADS_TEST_IDS.deliveryEvidence}>
                          <Typography variant="subtitle2" sx={{ mb: 0.75 }}>Delivery evidence</Typography>
                          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
                            Source: {tikTok.deliveryPreview.providerDiagnostics.sourceApiVersion ?? activeConnection.sourceApiVersion}
                            {tikTok.deliveryPreview.providerDiagnostics.bounded ? ' · bounded preview' : ''}
                          </Typography>
                          <Stack spacing={0.75}>
                            {tikTok.deliveryPreview.rows.slice(0, 12).map((row, index) => {
                              const evidence = deliveryEvidence(row);
                              return (
                                <Box key={`${evidence.id}-${index}`}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {evidence.label} · {evidence.id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {evidence.facts.length > 0 ? evidence.facts.join(' · ') : 'Provider metrics unavailable'}
                                  </Typography>
                                </Box>
                              );
                            })}
                          </Stack>
                          {tikTok.deliveryPreview.rows.length > 12 && (
                            <Typography variant="caption" color="text.secondary">
                              Showing 12 of {tikTok.deliveryPreview.rows.length} bounded delivery rows.
                            </Typography>
                          )}
                        </Box>
                      )}
                      {previewPartial && (
                        <Alert severity="warning">
                          Preview returned partial provider evidence. Review the typed warning before synchronizing.
                        </Alert>
                      )}
                      {tikTok.entityPreview && tikTok.entityPreview.entities.length === 0 && (
                        <Alert severity="info">No campaign, ad group, manual ad, or Smart+ entity was observed.</Alert>
                      )}
                      {tikTok.deliveryPreview && tikTok.deliveryPreview.rows.length === 0 && (
                        <Alert severity="info">No delivery rows were observed in this date window.</Alert>
                      )}
                    </Stack>
                  </Paper>
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  justifyContent="space-between"
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Box>
                    <Typography
                      variant="body2"
                      color={lastSyncStale ? 'warning.main' : 'text.secondary'}
                      data-testid={TIKTOK_ADS_TEST_IDS.lastSync}
                    >
                      Last successful sync: {formatTimestamp(activeConnection.lastSuccessfulManualSyncAt)}
                      {lastSyncStale ? ' · stale' : ''}
                    </Typography>
                    {tikTok.syncResult && (
                      <Typography variant="caption" color="text.secondary">
                        Latest request: {tikTok.syncResult.state} · {tikTok.syncResult.rowsObserved} rows observed
                      </Typography>
                    )}
                  </Box>
                  <Button
                    variant="contained"
                    startIcon={loadingIcon(tikTok.action === 'sync', <SyncIcon />)}
                    disabled={tikTok.action !== null || !runtime.manualSyncEnabled || !tikTok.entityPreview || !tikTok.deliveryPreview || previewPartial}
                    onClick={() => void tikTok.sync()}
                    data-testid={TIKTOK_ADS_TEST_IDS.syncButton}
                  >
                    Sync reporting data
                  </Button>
                </Stack>
                {!runtime.manualSyncEnabled && (
                  <Alert severity="info">
                    Manual synchronization is not enabled for this environment. Preview remains available.
                  </Alert>
                )}
              </Stack>
            )}
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}

export default TikTokAdsIntegrationContent;
